import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useRevalidator } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// Hooks
import { useSurveillanceRoutes } from './useSurveillanceRoutes';
import { useGpsGate } from './useGpsGate';
import { showSuccessToast } from '../utils/toasts';

// Redux
import { openModal, closeModal } from '../state/actions/ui/modal';
import { modalIds } from '../components/modals/SuperModal/types';

// Utils
import {
  getActiveSegment,
  directionNameKey,
  resumeSeedFromSegments,
} from '../utils/surveillanceRoute';

// Modal payload types
import { StartTimerConfirmPayload } from '../components/modals/submodals/NewSurveillanceSegmentModal/types';
import { PauseTimerConfirmPayload } from '../components/modals/submodals/PauseSurveillanceRouteModal/types';

// Types
import { SurveillanceRoute, RouteSegment } from '../types/surveillanceRoute';

/**
 * Task 3 (2026-07-07): extrae `handleOpenStartTimer` + `handleFinishRoute` de
 * `RoutePreviewPage` — envuelve `useSurveillanceRoutes` + `dispatch(openModal(...))`
 * con la misma lógica byte-a-byte, solo parametrizada por `route`/`segments` en
 * vez de leerlos del closure de la página.
 *
 * Tras la extracción, `RoutePreviewPage` ya no necesita llamar a
 * `useSurveillanceRoutes()` directamente — `startRoute`/`finishRoute`/
 * `createSegment`/`finishSegment` solo se usaban dentro de estos dos handlers;
 * el único valor derivado que la página seguía necesitando (`isLoading`,
 * unión de los 4 flags de loading para `RouteHeaderCard`) se expone aquí.
 */
export function useRouteTimerActions(
  route: SurveillanceRoute | undefined,
  segments: RouteSegment[]
) {
  const { t } = useTranslation(['pages', 'workOrders', 'toasts']);
  const dispatch = useDispatch();
  const revalidator = useRevalidator();
  const guardSegmentStart = useGpsGate();

  const {
    startRoute,
    finishRoute,
    createSegment,
    finishSegment,
    isStarting,
    isFinishing,
    isCreatingSegment,
    isFinishingSegment,
  } = useSurveillanceRoutes();

  const isLoading =
    isStarting || isFinishing || isCreatingSegment || isFinishingSegment;

  const activeSegment = useMemo(
    () => getActiveSegment({ segments }),
    [segments]
  );

  /**
   * Semilla del PK para el asistente del tramo: el PK con el que se cerró el
   * anterior.
   *
   * ⚠️ Desde aquí **no se llena nunca**, y no es un descuido: el botón que abre
   * este modal solo sale con el RV `open` (`RouteHeaderCard`), y un RV `open`
   * por definición no tiene ningún tramo — `syncStateFromSegments` lo pasa a
   * `paused` en cuanto existe uno. Se conserva porque el contrato del modal la
   * admite y cuesta cero; quien la llena de verdad es el propio modal al
   * reanudar desde el cronómetro, que es el único camino del resume.
   */
  const resumeSeed = useMemo(
    () => resumeSeedFromSegments(segments),
    [segments]
  );

  /**
   * Open the StartTimerModal to start an `open` route — the ONLY path left
   * for this case after Task 3 (review final C1): `TimerOverlay` únicamente
   * lista RVs in-progress/paused, así que un RV recién creado (open) nunca
   * aparece ahí y se quedaría sin forma de arrancar. Backend marca
   * `actualStartDate` (startRoute) y crea el primer tramo (createSegment);
   * el RV auto-transiciona a in-progress vía syncStateFromSegments.
   */
  const handleOpenStartTimer = useCallback(() => {
    if (!route) return;
    const abrirVentanaDelTramo = () =>
      dispatch(
        openModal(modalIds.NEW_SURVEILLANCE_SEGMENT, {
          isLoading: isCreatingSegment || isStarting,
          defaultPkInit: resumeSeed?.pkInit,
          // Contrato uniforme con el otro camino (el cronómetro): con el
          // `routeId` el modal puede resolver la semilla por su cuenta si aquí
          // no la hay — que es siempre, ver el comentario de `resumeSeed`.
          routeId: route.id,
          // Multi-carretera (2026-08-15): el modal busca el PK en TODAS y
          // resuelve en cuál está el operario.
          roadIds: route.roadIDs,
          // Vías de apoyo (2026-09-23): las de su concesión, si la patrulla
          // está fuera de las del recorrido.
          concessionIds: route.concessionIDs,
          // Desempate en el asfalto compartido: se sigue por donde se venía.
          previousRoadId: resumeSeed?.roadId ?? undefined,
          onConfirm: async (data: StartTimerConfirmPayload) => {
            try {
              // Cadena = UNA operación para el usuario (sr-offline-parity T5):
              // se silencian los toasts individuales y se emite uno solo al
              // final. Offline, cada paso encolado devuelve `null` (sentinel)
              // SIN lanzar → el paso 2 también se encola y el replay FIFO
              // converge (antes el throw cortaba y el recorrido quedaba sin
              // tramo tras el replay).
              const started = await startRoute(
                {
                  id: route.id,
                  registerInitDate: new Date().toISOString(),
                },
                { silentSuccess: true }
              );
              const created = await createSegment(
                {
                  surveillanceRouteId: route.id,
                  // Multi-carretera: la que eligió el operario en el modal. Sin
                  // esto el tramo nacía en la primera pasara lo que pasara, y el
                  // PK se juzgaba el resto de su vida contra la equivocada.
                  roadId: data.roadId,
                  direction: data.direction,
                  // Fix ronda 4: se persistía el value crudo ('top'/'down') y
                  // SegmentList lo prioriza como label → mostraba "top".
                  directionName: t(
                    directionNameKey(data.direction) ??
                      'pages:surveillance.directionUnspecified'
                  ),
                  // D1 (2026-07-23): el tramo nace SIN carril; se registra al cerrarlo.
                  // 2026-08-10: y SIN descripción, por el mismo motivo.
                  pkInitKm: data.pkInitKm,
                  pkInitMeter: data.pkInitMeter,
                },
                { silentSuccess: true }
              );
              showSuccessToast(
                started === null || created === null
                  ? t('modals:changeState.queuedOffline')
                  : t('toasts:success.updated')
              );
              dispatch(closeModal());
              revalidator.revalidate();
            } catch (error) {
              Sentry.captureException(error);
              console.error('Error starting timer:', error);
              // Sin toast aquí: useSurveillanceRoutes ya emite el de negocio
              // (backend-message-first, 2026-07-17) — no duplicar.
            }
          },
        })
      );

    // Sin permiso de GPS no se abre tramo. El modal bloqueante sale AQUÍ y
    // no un gesto más tarde, cuando ya habría un tramo abierto y vacío.
    void guardSegmentStart(abrirVentanaDelTramo);
  }, [
    route,
    guardSegmentStart,
    isCreatingSegment,
    isStarting,
    resumeSeed,
    startRoute,
    createSegment,
    dispatch,
    revalidator,
    t,
  ]);

  /**
   * Finish the surveillance route entirely. If a timer is still active,
   * open the PauseTimerModal to collect the closing PK **and the cause** from
   * the user (el modal pide causa/carril/detalle — se GUARDAN en el último
   * tramo; fix 2026-07-31, antes se descartaban y el tramo se cerraba a null).
   * Si la causa es "Actuación", el tramo queda "actuación pendiente" (CTA
   * Vincular) pero NO se abre el wizard: el recorrido se está cerrando.
   * If no active segment, finish immediately.
   */
  const handleFinishRoute = useCallback(async () => {
    if (!route) return;

    if (activeSegment) {
      // Collect closing PK via modal — on confirm close segment + finish route.
      // Sin `defaultPkEnd` (plan `rv-mapa-gps-tiempo-real`, Task B3): el
      // modal resuelve GPS→PK on-mount (`useGpsToPK`, umbral 300m de B1 +
      // fallback offline de B2) y rellena el campo en cuanto tiene un fix —
      // pasar un PK estático aquí sería, en el mejor caso, redundante y en
      // el peor, engañoso (ver el mismo fix en `TimerOverlay.handleFinishSegment`).
      dispatch(
        openModal(modalIds.PAUSE_SURVEILLANCE_ROUTE, {
          isLoading: isFinishingSegment,
          // El PK de cierre se juzga contra la carretera del TRAMO, no contra
          // la primera del recorrido.
          roadId: activeSegment?.roadId ?? route.roadIDs?.[0],
          // Para saber si hace falta decir en CUÁL: con una sola, no.
          roadIds: route.roadIDs,
          // Vías de apoyo (2026-09-23): el tramo puede acabar en una de su
          // concesión que el recorrido no declara.
          concessionIds: route.concessionIDs,
          // Para buscar en el rastro grabado dónde cruzó de carretera.
          routeId: route.id,
          segmentStartDate: activeSegment.startDate,
          // Su PK inicial: es donde arranca el primer trozo.
          segmentPkInit: {
            km: activeSegment.pkInitKm,
            meter: activeSegment.pkInitMeter,
          },
          onConfirm: async (data: PauseTimerConfirmPayload) => {
            try {
              // Cadena finalizar (T5): mismo patrón que iniciar — toast único
              // y sentinel `null` para que offline se encolen AMBOS pasos.
              const finishedSegment = await finishSegment(
                {
                  id: activeSegment.id,
                  endDate: new Date().toISOString(),
                  pkEndKm: data.pkEndKm,
                  pkEndMeter: data.pkEndMeter,
                  // Guardar la causa/carril/detalle que el modal SÍ pide (antes
                  // se descartaban → el último tramo perdía su motivo).
                  pauseCause: data.pauseCause,
                  pauseCauseDetail: data.pauseCauseDetail,
                  lane: data.lane,
                  // Y por qué carreteras pasó, si pasó por más de una.
                  roadStretches: data.roadStretches,
                },
                // `routeId`: si acabó en una vía de apoyo, pasa a ser del
                // recorrido también en la caché de la tablet.
                { silentSuccess: true, routeId: route.id }
              );
              const finishedRoute = await finishRoute(
                {
                  id: route.id,
                  registerEndDate: new Date().toISOString(),
                },
                { silentSuccess: true }
              );
              showSuccessToast(
                finishedSegment === null || finishedRoute === null
                  ? t('modals:changeState.queuedOffline')
                  : t('toasts:success.updated')
              );
              dispatch(closeModal());
              revalidator.revalidate();
            } catch (error) {
              Sentry.captureException(error);
              console.error('Error finishing route:', error);
              // Sin toast: lo emite useSurveillanceRoutes (no duplicar).
            }
          },
        })
      );
    } else {
      // No active segment — finish route directly
      try {
        await finishRoute({
          id: route.id,
          registerEndDate: new Date().toISOString(),
        });
        revalidator.revalidate();
      } catch (error) {
        Sentry.captureException(error);
        console.error('Error finishing route:', error);
        // Sin toast: lo emite useSurveillanceRoutes (no duplicar).
      }
    }
  }, [
    route,
    activeSegment,
    isFinishingSegment,
    finishSegment,
    finishRoute,
    dispatch,
    revalidator,
    t,
  ]);

  return {
    handleOpenStartTimer,
    handleFinishRoute,
    isLoading,
  };
}

export default useRouteTimerActions;
